/**
 * Path-based multi-tenancy helpers.
 *
 * Emergent Deploy doesn't support wildcard subdomains (Feb 2026 confirmation),
 * so tenants are identified by the first `/t/<slug>/` path segment instead of
 * the subdomain. All authenticated URLs live under `/t/:tenantSlug/*`.
 *
 * Marketing routes (`/`, `/pricing`, `/contact`) and the super-admin console
 * (`/super-admin`) live at the ROOT — they intentionally have no tenant slug.
 *
 * Usage:
 *   const tp = useTenantPath();
 *   <Link to={tp('/dashboard')} />          // → /t/nilkamal/dashboard
 *   navigate(tp('/leads/xyz'))
 *
 * For non-hook contexts (axios interceptors, etc.), `getSlugFromLocation()`
 * reads the slug directly off `window.location.pathname`.
 */

import { useParams } from 'react-router-dom';

/** Parse the tenant slug from a pathname string. Returns '' when not present. */
export function parseSlugFromPathname(pathname) {
  if (!pathname) return '';
  const m = pathname.match(/^\/t\/([a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?)(?:\/|$)/i);
  return m ? m[1].toLowerCase() : '';
}

/** Non-hook slug reader — safe to call from anywhere (axios interceptors etc). */
export function getSlugFromLocation() {
  if (typeof window === 'undefined') return '';
  return parseSlugFromPathname(window.location.pathname);
}

/** Build a tenant-scoped path. `path` must begin with '/'. */
export function buildTenantPath(slug, path) {
  if (!slug) return path;                            // no tenant context yet — leave path alone
  if (!path || !path.startsWith('/')) return path;   // guardrail: only rewrite absolute-ish paths
  if (path.startsWith(`/t/`)) return path;           // already namespaced — don't double-wrap
  return `/t/${slug}${path}`;
}

/**
 * React hook — returns a `tp(path)` function bound to the current tenant slug
 * (from the `:tenantSlug` param on the enclosing Route). If the current route
 * has no slug in it, `tp(path)` returns `path` unchanged — this makes it safe
 * to use inside components that render both inside and outside a tenant.
 */
export function useTenantPath() {
  const params = useParams();
  const slug = (params.tenantSlug || '').toLowerCase();
  return (path) => buildTenantPath(slug, path);
}

/** Same idea, but returns the slug itself. */
export function useTenantSlug() {
  const params = useParams();
  return (params.tenantSlug || '').toLowerCase();
}
